import React from 'react';
import styled from 'styled-components';
import {
  IPriceCalculator,
  ISubscriptionState,
  PaymentPlanType,
  SubscriptionType,
  UserType,
} from '../../constants/types';
import { compose, withState } from 'recompose';
import {
  getSubscriptionIds,
  getSubscriptionsForAction,
} from '../../constants/payment';
import { RouterProps, withRouter } from 'react-router';
import {
  cancelSubscriptionMutation,
  getUserAccessQuery,
} from '../../constants/gql/graphqlDefaultRequests';
import {
  GET_PLAN_TYPES_FOR_USER_ROLE,
  GET_SUITABLE_PLANS,
} from '../../pages/Landing/LnPricing/constants';
import PricingPlan from '../../pages/Landing/LnPricing/Plans/PricingPlan';
import PatientPlanCard, {
  SubscriptionCardSkeleton,
} from '../../redesign/Subsciptions/Subscription';
import {
  CURRENT_PLAN,
  CANCELED_PLAN,
  PLAN_BLOCKED,
  PENDING_PLAN,
} from '../../pages/Landing/LnPlatform/constants';
import {
  SUBSCRIPTION_HOST_MSG,
  subscriptionHostLocalisation,
} from '../../constants/localization';
import { UserAccess } from 'menhealth-subscription-validation';
import ConfirmSubscriptionModal from '../../redesign/Subsciptions/modals/ConfirmSubscriptionModal';
import {
  CANCEL_SUBSCRIPTION_MSG,
  CHANGE_PAYMENT_METHOD_MSG,
  CONFIRM_CANCEL_SUBSCRIPTION_MSG,
  STOP_RENEW_SUBSCRIPTION_MSG,
} from '../../constants/localization';
import ChangePaymentMethodModal from '../../redesign/Subsciptions/modals/ChangePaymentMethodModal';
import { graphql } from 'react-apollo';
import {
  ACTIVE_USER_SUBSCRIPTIONS,
  PATIENTS_COUNT_FOR_DOCTOR,
  PAYMENT_PLANS_BY_ROLE,
} from '../../constants/gql/payment';
import { OptionProps } from '@apollo/react-hoc/lib/types';
import CurrentSubscription from '../CurrentSubscription';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  font-family: ${(props) => props.theme.fonts.regular};
  width: 100%;
`;

const Plans: React.FunctionComponent<IProps> = ({
  plans,
  user,
  priceCalculator,
  subscriptionState,
  switchAnotherPlan,
  forceEnableRenewal = false,
  switchMessage,
  confirmCancelSubIsOpen,
  toggleConfirmCancelSub,
  toggleChangePaymentMethod,
  changePaymentMethodModalIsOpen,
  activeSubscriptions,
  cancelSubscriptions,
  maxPatients,
  loadingPlans,
  loadingActiveSubscriptions,
  loadingUserHasSubscription,
}) => {
  const isLoading =
    loadingPlans || loadingActiveSubscriptions || loadingUserHasSubscription;


  if (isLoading || !plans) {
    return (
      <Wrapper>
        <SubscriptionCardSkeleton />
      </Wrapper>
    );
  }

  if (!plans || plans.length === 0) {
    return (
      <Wrapper>
        <div style={{ padding: '20px', color: 'red' }}>
          <h3>DEBUG: No plans found for role {user.role}</h3>
          <p>Plans array: {JSON.stringify(plans)}</p>
          <p>Loading: {isLoading ? 'true' : 'false'}</p>
        </div>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      {
        <>
          {plans.map((paymentPlanType) => {
              const isIOSSubscription =
                !!activeSubscriptions?.[0] &&
                activeSubscriptions[0].subscriptionHost === 'IOS';
              const isRenewableSubscription =
                subscriptionState.isRenewableSubscriptionPlan(paymentPlanType);
              const isActivePlan =
                subscriptionState.isActivePlan(paymentPlanType);
              const isPendingPlan =
                subscriptionState.isPendingPlan(paymentPlanType);
              const isBlockedPlan =
                subscriptionState.isBlocked(paymentPlanType);
              const isCancelable =
                subscriptionState.isCancelable(paymentPlanType);
              const isPaymentMethodChangeable =
                subscriptionState.isPaymentMethodChangeable(paymentPlanType);
              const chooseDisabled =
                subscriptionState.isDisabled(paymentPlanType) ||
                isActivePlan ||
                isBlockedPlan;

              const label = subscriptionState.isPendingPlan(paymentPlanType)
                ? PENDING_PLAN
                : isActivePlan
                ? CURRENT_PLAN
                : isBlockedPlan
                ? PLAN_BLOCKED
                : isRenewableSubscription
                ? CANCELED_PLAN
                : '';

              const planPrice = priceCalculator.getPrice(paymentPlanType);
              const planHeader = priceCalculator.getHeader(paymentPlanType);
              const planDescription =
                priceCalculator.getDescription(paymentPlanType);

              if (user.role === 'PATIENT' || user.role === 'DOCTOR' || user.role === 'CLINIC') {
                const secondaryActions: Array<{
                  label: string;
                  onClick: () => void;
                  tone?: 'default' | 'danger';
                }> = [];

                if (isPaymentMethodChangeable && !isIOSSubscription) {
                  secondaryActions.push({
                    label: CHANGE_PAYMENT_METHOD_MSG,
                    onClick: () => toggleChangePaymentMethod(true),
                  });
                }

                if (isCancelable && !isIOSSubscription) {
                  secondaryActions.push({
                    label: CANCEL_SUBSCRIPTION_MSG,
                    onClick: () => toggleConfirmCancelSub(true),
                    tone: 'danger',
                  });
                }

                // Get subscription platform for disclaimer
                const subscriptionPlatform = activeSubscriptions?.[0]?.subscriptionHost
                  ? `${SUBSCRIPTION_HOST_MSG}${subscriptionHostLocalisation[activeSubscriptions[0].subscriptionHost] || activeSubscriptions[0].subscriptionHost}`
                  : undefined;

                // For doctors and clinics, features are the same as planDescription
                const features = planDescription;

                const getPlanSubtitle = () => {
                  if (user.role === 'DOCTOR') return 'Physician Subscription Plan';
                  if (user.role === 'CLINIC') return 'Clinic Subscription Plan';
                  return 'Patient Self Monitoring Plan';
                };

                return (
                  <PatientPlanCard
                    key={paymentPlanType.planId}
                    planId={paymentPlanType.planId}
                    price={planPrice}
                    currency={paymentPlanType.currency}
                    role={user.role}
                    planTitle={planHeader}
                    planSubtitle={getPlanSubtitle()}
                    features={features}
                    isActivePlan={isActivePlan}
                    isPendingPlan={isPendingPlan}
                    isRenewableSubscription={isRenewableSubscription}
                    isBlockedPlan={isBlockedPlan}
                    isIOSSubscription={isIOSSubscription}
                    chooseDisabled={chooseDisabled}
                    label={label}
                    maxPatients={maxPatients}
                    switchAction={
                      switchAnotherPlan && switchMessage
                        ? {
                            label: switchMessage,
                            onClick: () => switchAnotherPlan(),
                          }
                        : undefined
                    }
                    secondaryActions={secondaryActions}
                    forceEnableRenewal={forceEnableRenewal}
                    disclaimer={subscriptionPlatform}
                  />
                );
              }

              return (
                <PricingPlan
                  isRenewableSubscription={isRenewableSubscription}
                  isIOSSubscription={isIOSSubscription}
                  isCancelable={isCancelable}
                  isPaymentMethodChangeable={isPaymentMethodChangeable}
                  isActivePlan={isActivePlan}
                  isPendingPlan={isPendingPlan}
                  planId={paymentPlanType.planId}
                  key={paymentPlanType.planId}
                  price={planPrice}
                  header={planHeader}
                  description={planDescription}
                  isLabeled={
                    isActivePlan || isRenewableSubscription || isBlockedPlan
                  }
                  label={label}
                  chooseDisabled={chooseDisabled}
                  currency={paymentPlanType.currency}
                  role={user.role}
                  maxPatients={maxPatients}
                  switchAnotherPlan={switchAnotherPlan}
                  switchMessage={switchMessage}
                  openChangePaymentMethodModal={() =>
                    toggleChangePaymentMethod(true)
                  }
                  openConfirmCancelSubModal={() => toggleConfirmCancelSub(true)}
                  forceEnableRenewal={forceEnableRenewal}
                />
              );
            })}
          {/* CurrentSubscription removed - subscription platform now shown in card disclaimer */}
          <ConfirmSubscriptionModal
            isOpen={confirmCancelSubIsOpen}
            onClose={() => toggleConfirmCancelSub(false)}
            title={STOP_RENEW_SUBSCRIPTION_MSG}
            description={CONFIRM_CANCEL_SUBSCRIPTION_MSG}
            confirmLabel={CANCEL_SUBSCRIPTION_MSG}
            tone="danger"
            onConfirm={() =>
              cancelSubscriptions(
                getSubscriptionIds(
                  activeSubscriptions.filter((s) => s.status !== 'Canceled'),
                ),
              )
            }
          />
          <ChangePaymentMethodModal
            isOpen={changePaymentMethodModalIsOpen}
            onClose={() => toggleChangePaymentMethod(false)}
          />
        </>
      }
    </Wrapper>
  );
};

export default compose<IPlansPropsInner, IPlansPropsOuter>(
  getUserAccessQuery<IPlansPropsInner>(),
  graphql(ACTIVE_USER_SUBSCRIPTIONS, {
    options: { fetchPolicy: 'cache-and-network' },
    props: ({ data }: OptionProps<IProps>) => {
      const subscriptionsForActions: SubscriptionType[] | null =
        data.activeUserSubscriptions &&
        getSubscriptionsForAction(data.activeUserSubscriptions);
      return {
        activeSubscriptions: subscriptionsForActions,
        loadingActiveSubscriptions: data.loading,
        allValidSubscriptions: data.activeUserSubscriptions,
      };
    },
  }),
  graphql(PAYMENT_PLANS_BY_ROLE, {
    skip: ({ loadingActiveSubscriptions }: IProps) =>
      loadingActiveSubscriptions,
    options: ({ activeSubscriptions, user }: IProps) => ({
      variables: {
        role: user.role,
        planId:
          activeSubscriptions &&
          activeSubscriptions[0] &&
          activeSubscriptions[0].planId,
      },
    }),
    props: ({ ownProps, data }: OptionProps<IProps>) => {
      const code = ownProps.user.promoCode;

      let planId = null;

      const clinicWithPlan = ownProps.user.clinics?.find(
        (clinic) => clinic.plan?.planId,
      );

      if (clinicWithPlan) {
        planId = clinicWithPlan.plan.planId;
      }

      const doctorWithPlan = ownProps.user.doctors?.find(
        (doctor) => doctor.plan?.planId,
      );

      if (doctorWithPlan) {
        planId = doctorWithPlan.plan.planId;
      }

      const userPlanId = ownProps.user.plan?.planId;

      if (userPlanId) {
        planId = userPlanId;
      }

      return {
        plans: !data.loading
          ? GET_SUITABLE_PLANS(data.plans, code, planId, ownProps.user.role).filter((p) => {
              return GET_PLAN_TYPES_FOR_USER_ROLE(ownProps.user.role).includes(
                p.planType,
              );
            })
          : null,
        loadingPlans: data.loading,
      };
    },
  }),
  withRouter,
  withState('isConfirmDisabled', 'setIsConfirmDisabled', true),
  withState('confirmCancelSubIsOpen', 'toggleConfirmCancelSub', false),
  withState('isPaymentModalOpen', 'setPaymentModalOpen', false),
  withState('changePlanIsOpen', 'toggleChangePlan', false),
  withState(
    'changePaymentMethodModalIsOpen',
    'toggleChangePaymentMethod',
    false,
  ),
  cancelSubscriptionMutation,
)(Plans);

type IProps = IPlansPropsInner & IPlansPropsOuter;

interface IPlansPropsInner extends RouterProps {
  activeSubscriptions?: SubscriptionType[];
  allValidSubscriptions?: SubscriptionType[];
  loadingActiveSubscriptions: boolean;
  userAccess: UserAccess;
  toggleConfirmCancelSub: (value: boolean) => void;
  confirmCancelSubIsOpen: boolean;
  toggleChangePaymentMethod: (value: boolean) => void;
  changePaymentMethodModalIsOpen: boolean;
  cancelSubscriptions: (ids: string[]) => Promise<void>;
  isConfirmDisabled: boolean;
  setIsConfirmDisabled: (value: boolean) => void;
  planDisabledForChoose: boolean;
  isRenewedSubscription: boolean;
  setIsRenewedSubscription: (value: boolean) => void;
  loadingUserHasSubscription: boolean;
  plans: PaymentPlanType[];
  loadingPlans: boolean;
}

interface IPlansPropsOuter {
  user: UserType;
  maxPatients: number;
  priceCalculator: IPriceCalculator;
  switchAnotherPlan?: () => void;
  subscriptionState: ISubscriptionState;
  switchMessage?: string;
  forceEnableRenewal?: boolean;
}
